import { useState, useRef, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ImageGalleryProps {
  mainImage: string;
  images: string[];
  productName: string;
  badge?: ReactNode;
}

export default function ImageGallery({ mainImage, images, productName, badge }: ImageGalleryProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(mainImage);
  const [zoomed, setZoomed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const allImages = [mainImage, ...images];
  const selectedIndex = allImages.indexOf(selected);

  const touchStartX = useRef<number | null>(null);
  const touchCurrentX = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (index < 0) index = allImages.length - 1;
      if (index >= allImages.length) index = 0;
      setSelected(allImages[index]);
    },
    [allImages]
  );

  const goNext = useCallback(() => goTo(selectedIndex + 1), [goTo, selectedIndex]);
  const goPrev = useCallback(() => goTo(selectedIndex - 1), [goTo, selectedIndex]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    touchCurrentX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current == null || touchCurrentX.current == null) {
      setIsDragging(false);
      return;
    }
    const deltaX = touchStartX.current - touchCurrentX.current;
    const threshold = 50;
    if (deltaX > threshold) {
      goNext();
    } else if (deltaX < -threshold) {
      goPrev();
    }
    touchStartX.current = null;
    touchCurrentX.current = null;
    setIsDragging(false);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Main Image */}
      <div
        className="relative rounded-2xl overflow-hidden bg-[#F0EFED] w-full aspect-square flex items-center justify-center select-none touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => !isDragging && setZoomed(true)}
      >
        <img
          src={selected}
          alt={productName}
          draggable={false}
          className="w-full h-full object-cover transition-transform duration-300"
        />

        {badge && <div className="absolute top-4 right-4">{badge}</div>}

        {/* Mobile swipe hint */}
        <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[10px] text-[#8A8A8A] font-light bg-white/80 px-2.5 py-1 rounded-full">
          <i className="ri-arrow-left-right-line"></i>
          <span>{t('products.swipeToBrowse', 'Swipe')}</span>
        </div>

        {/* Zoom hint (desktop only) */}
        <span className="hidden md:inline absolute bottom-3 right-3 text-[10px] text-[#8A8A8A] font-light bg-white/80 px-2 py-1 rounded-full">
          {t('products.clickToZoom')}
        </span>

        {/* Prev/Next arrows (desktop hover) */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white items-center justify-center transition-colors cursor-pointer"
              aria-label="Previous image"
            >
              <i className="ri-arrow-left-s-line text-[#2A2A2A]"></i>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white items-center justify-center transition-colors cursor-pointer"
              aria-label="Next image"
            >
              <i className="ri-arrow-right-s-line text-[#2A2A2A]"></i>
            </button>
          </>
        )}
      </div>

      {/* Pagination dots (mobile) */}
      {allImages.length > 1 && (
        <div className="flex md:hidden items-center justify-center gap-2">
          {allImages.map((img, i) => (
            <button
              key={i}
              onClick={() => setSelected(img)}
              className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                selected === img ? 'bg-[#7B1113] w-5' : 'bg-[#E8E8E6]'
              }`}
              aria-label={`View ${productName} image ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Thumbnails (desktop + mobile scroll) */}
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
        {allImages.map((img, i) => (
          <button
            key={i}
            onClick={() => setSelected(img)}
            className={`snap-start w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 bg-[#F0EFED] transition-all cursor-pointer ${
              selected === img ? 'border-[#7B1113]' : 'border-transparent hover:border-[#8A8A8A]/30'
            }`}
          >
            <img
              src={img}
              alt={`${productName} view ${i + 1}`}
              draggable={false}
              className="w-full h-full object-contain p-1.5"
            />
          </button>
        ))}
      </div>

      {/* Zoom Modal */}
      {zoomed && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-8 cursor-zoom-out"
          onClick={() => setZoomed(false)}
        >
          <img
            src={selected}
            alt={productName}
            draggable={false}
            className="max-w-full max-h-full object-contain"
          />
          <button
            onClick={() => setZoomed(false)}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      )}
    </div>
  );
}